import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type WorkObjectComment = Awaited<ReturnType<typeof trpc.workObjectComments.list.query>>[number];
export type WorkObjectCommentAttachment = WorkObjectComment["attachments"][number];

export function useWorkObjectComments(workObjectId: string | null) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const listQuery = useQuery(trpc.workObjectComments.list.queryOptions(workObjectId ? { workObjectId } : skipToken));
  const createMutation = useMutation(trpc.workObjectComments.create.mutationOptions());
  const updateMutation = useMutation(trpc.workObjectComments.update.mutationOptions());
  const deleteMutation = useMutation(trpc.workObjectComments.delete.mutationOptions());
  const reactionMutation = useMutation(trpc.workObjectComments.toggleReaction.mutationOptions());

  function upsertComment(comment: WorkObjectComment) {
    if (!workObjectId) return;
    queryClient.setQueryData(trpc.workObjectComments.list.queryKey({ workObjectId }), (current) => {
      const list = current ?? [];
      const index = list.findIndex((entry) => entry.id === comment.id);
      if (index === -1) return [...list, comment];
      const next = [...list];
      next[index] = comment;
      return next;
    });
  }

  async function createComment(input: { attachmentIds: string[]; body: Record<string, unknown>; replyToCommentId: string | null }) {
    if (!workObjectId) throw new Error("Work object is not selected");
    const comment = await createMutation.mutateAsync({ ...input, workObjectId });
    upsertComment(comment);
    return comment;
  }

  async function updateComment(input: { attachmentIds: string[]; body: Record<string, unknown>; commentId: string }) {
    const comment = await updateMutation.mutateAsync(input);
    upsertComment(comment);
    return comment;
  }

  async function deleteComment(commentId: string) {
    await deleteMutation.mutateAsync({ commentId });
    if (!workObjectId) return;
    queryClient.setQueryData(trpc.workObjectComments.list.queryKey({ workObjectId }), (current) =>
      (current ?? []).filter((entry) => entry.id !== commentId),
    );
  }

  async function toggleReaction(commentId: string, emoji: string) {
    const comment = await reactionMutation.mutateAsync({ commentId, emoji });
    upsertComment(comment);
    return comment;
  }

  return {
    createComment,
    data: listQuery.data ?? [],
    deleteComment,
    error: listQuery.error instanceof Error ? listQuery.error.message : listQuery.error ? String(listQuery.error) : null,
    isCreating: createMutation.isPending,
    isLoading: listQuery.isPending,
    toggleReaction,
    updateComment,
  };
}
